import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {selectTests} from '../../slices/testSlice';
import {useDashboardData} from '../../hooks/useDashboardData';
import {getRecentBiomarkers, calculateTrendChange, cleanBiomarkerName} from '../../utils/biomarkerUtils';
import {filterTestsByCategory, HEALTH_CATEGORIES} from '@/utils/healthCategories';
import TrendCard from './TrendCard';
import SectionContainer from '../common/SectionContainer';

interface TrendData {
    name: string;
    value: string;
    change: string;
    changeType: 'up' | 'down';
}

interface LabTrendsProps {
    selectedCategory: string;
    onTrendSelect?: (biomarkerName: string) => void;
}

const LabTrends: React.FC<LabTrendsProps> = ({selectedCategory, onTrendSelect}) => {
    const testsState = useSelector(selectTests);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: tests} = useDashboardData();
    const isLoading = testsState.loading;

    // Get all results from tests and filter by category
    const allResults = tests.flatMap(
        (test: any) =>
            test.results?.map((result: any) => ({
                ...result,
                createdDate: test.createdDate,
            })) || []
    );

    // Filter by selected category
    const categoryResults = filterTestsByCategory(allResults, selectedCategory);

    // Extract test names from category results to filter biomarkers
    const categoryTestNames = new Set(categoryResults.map((result: any) => result.test_name));

    // Get the top 12 most recent biomarkers, filtered by category
    const allRecentBiomarkers = getRecentBiomarkers(tests, 50); // Get more initially
    const recentBiomarkers = allRecentBiomarkers
        .filter((biomarker) => categoryTestNames.has(biomarker.testName))
        .slice(0, 12); // Then take top 12 from the category

    // Convert to trend data format
    const trends: TrendData[] = recentBiomarkers.map((biomarker) => {
        const trendChange = calculateTrendChange(tests, biomarker.testName);

        return {
            name: cleanBiomarkerName(biomarker.testName),
            value: biomarker.result,
            change: trendChange.changeText,
            changeType: trendChange.changeType,
        };
    });

    // Responsive navigation functions
    const getItemsPerView = () => {
        if (typeof window === 'undefined') return 4;
        if (window.innerWidth < 768) return 4; // Mobile: 4 items vertically
        if (window.innerWidth < 1024) return 3; // Tablet: 3 items
        return 4; // Desktop: 4 items
    };

    const itemsPerView = getItemsPerView();
    const maxIndex = Math.max(0, trends.length - itemsPerView);

    const scrollLeft = () => {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
    };

    const scrollRight = () => {
        setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
    };

    const scrollUp = () => {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
    };

    const scrollDown = () => {
        setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
    };

    // Reset index when trends change
    React.useEffect(() => {
        setCurrentIndex(0);
    }, [trends.length, selectedCategory]);

    const categoryName = HEALTH_CATEGORIES[selectedCategory]?.name || 'Health';

    return (
        <SectionContainer title={`Your ${categoryName} Lab Trends`}>
            <div className="relative">
                {isLoading ? (
                    <div className="text-center py-8 text-mld-card-text">Loading lab trends...</div>
                ) : trends.length === 0 ? (
                    <div className="text-center py-8 text-mld-card-text">No lab trends available</div>
                ) : (
                    <>
                        {/* Mobile: Up/Down navigation */}
                        <div className="md:hidden">
                            {trends.length > itemsPerView && (
                                <>
                                    <button
                                        onClick={scrollUp}
                                        disabled={currentIndex === 0}
                                        className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                        <svg
                                            className="w-4 h-4 text-mld-card-text"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 15l7-7 7 7"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={scrollDown}
                                        disabled={currentIndex >= maxIndex}
                                        className="absolute -bottom-1 left-1/2 -translate-x-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                        <svg
                                            className="w-4 h-4 text-mld-card-text"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Tablet/Desktop: Left/Right navigation */}
                        <div className="hidden md:block">
                            {trends.length > itemsPerView && (
                                <>
                                    <button
                                        onClick={scrollLeft}
                                        disabled={currentIndex === 0}
                                        className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                        <svg
                                            className="w-4 h-4 text-mld-card-text"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 19l-7-7 7-7"
                                            />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={scrollRight}
                                        disabled={currentIndex >= maxIndex}
                                        className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                        <svg
                                            className="w-4 h-4 text-mld-card-text"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                            />
                                        </svg>
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Mobile: Vertical stack */}
                        <div className="md:hidden">
                            <div className="flex flex-col gap-3 py-4">
                                {trends.slice(currentIndex, currentIndex + itemsPerView).map((trend, index) => (
                                    <div key={currentIndex + index} className="w-full">
                                        <TrendCard trend={trend} onClick={() => onTrendSelect?.(trend.name)} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Tablet: 3 items horizontally */}
                        <div className="hidden md:block lg:hidden">
                            <div className="flex gap-1 justify-center overflow-hidden px-8 py-4">
                                {trends.slice(currentIndex, currentIndex + 3).map((trend, index) => (
                                    <TrendCard
                                        key={currentIndex + index}
                                        trend={trend}
                                        onClick={() => onTrendSelect?.(trend.name)}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Desktop: 4 items horizontally */}
                        <div className="hidden lg:block">
                            <div className="flex gap-3 justify-center overflow-hidden px-12 py-4">
                                {trends.slice(currentIndex, currentIndex + 4).map((trend, index) => (
                                    <TrendCard
                                        key={currentIndex + index}
                                        trend={trend}
                                        onClick={() => onTrendSelect?.(trend.name)}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </SectionContainer>
    );
};

export default LabTrends;
